import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { PageHeader, Panel, Loading, Empty, Plate, Chip, NSelect, VehicleStatus, useFetch } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DocChip, LicChip, VehicleDocuments, LicencePanel } from "@/components/Documents";
import { fDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LEVEL_CLS = { expire: "border-rose-300 bg-rose-50 text-rose-800", manquant: "border-slate-300 bg-slate-50 text-slate-700", bientot: "border-amber-300 bg-amber-50 text-amber-800" };
const LEVEL_TXT = { expire: "Expiré", manquant: "Manquant", bientot: "Bientôt expiré" };

export default function DocumentsPage() {
  const { data, loading, reload } = useFetch("/documents/overview");
  const { real_role } = useAuth().user;
  const [open, setOpen] = useState(null);
  const [scope, setScope] = useState("actifs");
  if (loading || !data) return <Loading />;
  const close = () => {
    setOpen(null);
    reload();
  };
  const active = data.vehicles.filter((v) => (scope === "tous" ? true : scope === "archives" ? v.status === "archive" : v.status !== "archive"));
  return (
    <div data-testid="documents-page">
      <PageHeader overline="Administratif" title="Documents & permis" />
      <Panel title={`Alertes (${data.alerts.length}) — seuils J-${data.alert_days.join(" / J-")}`} className="mb-6" testid="documents-alerts">
        {!data.alerts.length ? <Empty text="Aucune alerte : tous les documents sont à jour" /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">{data.alerts.map((a, i) => (
            <button key={i} onClick={() => setOpen(a.kind === "vehicle" ? { kind: "vehicle", id: a.id, title: a.label } : { kind: "licence", owner_type: a.owner_type, id: a.id, title: a.label })}
              className={`text-left border rounded-lg p-3 text-sm ${LEVEL_CLS[a.level]}`} data-testid={`doc-alert-${i}`}>
              <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="w-4 h-4" /> {LEVEL_TXT[a.level]}{a.stage && a.level === "bientot" ? ` · ${a.stage}` : ""}</div>
              <div>{a.label}</div>
              {a.valid_until && <div className="text-xs opacity-80">Échéance : {fDate(a.valid_until)}</div>}
            </button>
          ))}</div>
        )}
      </Panel>
      <div className="grid xl:grid-cols-2 gap-6">
        <Panel title="Véhicules" flush testid="documents-vehicles" action={<div className="w-36"><NSelect value={scope} onChange={setScope} testid="documents-vehicle-scope" options={[{ value: "actifs", label: "Actifs" }, { value: "archives", label: "Archivés" }, { value: "tous", label: "Tous" }]} /></div>}>
          <div className="overflow-x-auto"><table className="tbl w-full">
            <thead><tr><th>Véhicule</th><th>Type</th><th>Carte grise</th><th>Assurance</th><th>Global</th></tr></thead>
            <tbody>{active.map((v) => (
              <tr key={v.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen({ kind: "vehicle", id: v.id, title: `${v.plate} — ${v.brand} ${v.model}` })} data-testid={`documents-vehicle-${v.plate}`}>
                <td><Plate>{v.plate}</Plate>{v.status !== "actif" && <span className="ml-1"><VehicleStatus v={v.status} /></span>}</td><td>{v.type === "livraison" ? "Livraison" : "Commercial"}</td>
                <td><DocChip v={v.documents.items.carte_grise.level} stage={v.documents.items.carte_grise.stage} /></td>
                <td><DocChip v={v.documents.items.assurance.level} stage={v.documents.items.assurance.stage} /></td>
                <td><DocChip v={v.documents.status} /></td>
              </tr>
            ))}</tbody>
          </table></div>
        </Panel>
        <Panel title="Permis de conduire" flush testid="documents-licences">
          <div className="overflow-x-auto"><table className="tbl w-full">
            <thead><tr><th>Salarié</th><th>Fonction</th><th>Statut permis</th><th>Validité</th></tr></thead>
            <tbody>{data.people.filter((p) => !["archive", "supprime"].includes(p.person_status)).map((p) => (
              <tr key={p.owner_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen({ kind: "licence", owner_type: p.owner_type, id: p.owner_id, title: `Permis — ${p.name}` })} data-testid={`documents-person-${p.owner_id}`}>
                <td className="font-semibold">{p.name}</td><td>{p.role}{p.person_status !== "actif" && <Chip cls="ml-1 bg-slate-100 border-slate-200 text-slate-500">{p.person_status}</Chip>}</td>
                <td><LicChip v={p.licence.status} stage={p.licence.stage} /></td><td>{fDate(p.licence.valid_until)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </Panel>
      </div>
      {open && (
        <Dialog open onOpenChange={close}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="documents-dialog">
            <DialogHeader className="px-5 pt-5"><DialogTitle className="font-display text-[#0E2D43]">{open.title}</DialogTitle></DialogHeader>
            {open.kind === "vehicle" ? <VehicleDocuments vehicleId={open.id} canEdit={real_role !== "direction"} /> : <LicencePanel ownerType={open.owner_type} ownerId={open.id} />}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
